import ButtonsBlock from '@/components/common/buttons-block/buttons-block';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import FormInputsSeparator from '@/components/common/form-inputs-separator/form-inputs-separator';
import useGetCurrentUserV2 from '@/hooks/useGetCurrentUser';
import useGetGroupAvatars from '@/hooks/useGetGroupAvatars';
import useGetManagerProfile from '@/hooks/useGetManagerProfile';
import {
    BIG_AGENCY_ADMIN,
    BIG_AGENCY_MANAGER,
    BIG_SUB_MANAGER_CLIENT_TAG,
    CREATE_AGENCY,
    DEFAULT_CLIENTS_GROUP_AVATAR_ALPHA_ID,
    GROUP_USERS,
    INCIDENT_SERVICE_AGENCY,
    SUB_MANAGER_TAG,
    USERS,
} from '@/utils/CONST';
import useGetAlphaId from '@/utils/useGetAlphaId';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { Button, email, Form, required, TextInput, useCreate, useNotify, useTranslate, useUpdate } from 'react-admin';

interface CreateAgencyDialogProps {
    open: boolean;
    onClose: () => void;
}

const CreateAgencyDialog = ({ open, onClose }: CreateAgencyDialogProps) => {
    const [create, { isLoading: isLoadingCreate }] = useCreate();
    const [, { isLoading: isLoadingUpdate }] = useUpdate();
    const translate = useTranslate();
    const notify = useNotify();
    const { currentUser, isLoading: isLoadingUser } = useGetCurrentUserV2();
    const { getAlphaId, isLoadingGetAlphaId } = useGetAlphaId({
        service: INCIDENT_SERVICE_AGENCY,
    });
    const queryClient = useQueryClient();
    const { profile, isLoading: isLoadingProfile } = useGetManagerProfile({
        profileName: BIG_AGENCY_MANAGER,
    });
    const { isLoading: isLoadingDefaultClientsGroup } = useGetGroupAvatars({
        filters: {
            alphaId: DEFAULT_CLIENTS_GROUP_AVATAR_ALPHA_ID,
        },
    });

    const handleCreateAgency = (values: any) => {
        const { agencyName, ...userValues } = values;
        const user_tags = [SUB_MANAGER_TAG, BIG_SUB_MANAGER_CLIENT_TAG, BIG_AGENCY_MANAGER, BIG_AGENCY_ADMIN];

        create(
            USERS,
            {
                data: {
                    ...userValues,
                    username: userValues.email,
                    company: currentUser?.company,
                    tags: user_tags,
                    profiles: [profile?.id],
                },
                meta: {
                    realResource: `v2/${USERS}`,
                },
            },
            {
                onSuccess: ({ id: userId }) => {
                    getAlphaId().then((data: { counter: number }) => {
                        create(
                            CREATE_AGENCY,
                            {
                                data: {
                                    name: agencyName,
                                    admin: userId,
                                    identifier: `big:agency:${data.counter}`,
                                },
                            },
                            {
                                onSuccess: () => {
                                    queryClient.invalidateQueries({
                                        queryKey: [GROUP_USERS, 'getInfiniteList'],
                                    });
                                    notify('ra.notification.created', { type: 'success' });
                                    onClose();
                                },
                            },
                        );
                    });
                },
                onError: (error: any) => {
                    const status = error?.response?.status ?? error?.status;
                    if (status === 400) {
                        notify('messages.ConstraintViolationList', { type: 'error' });
                    } else {
                        notify('ra.notification.error', { type: 'error' });
                    }
                },
            },
        );
    };

    return (
        <DialogTemplate title={translate('agency.form.title')} open={open} onClose={onClose}>
            <Form onSubmit={handleCreateAgency} reValidateMode={'onBlur'}>
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                    }}
                >
                    <FormInputsSeparator>
                        <TextInput
                            helperText={false}
                            name={'agencyName'}
                            source={'agencyName'}
                            label={'agency.form.agency_name'}
                            validate={[required()]}
                        />

                        <Typography fontSize={12}>{translate('agency.form.user_data')}</Typography>

                        <TextInput
                            helperText={false}
                            name={'lastName'}
                            source={'lastName'}
                            label={'main.navigation.last_name'}
                            validate={[required()]}
                        />

                        <TextInput
                            helperText={false}
                            name={'firstName'}
                            source={'firstName'}
                            label={'main.navigation.name'}
                            validate={[required()]}
                        />

                        <TextInput
                            helperText={false}
                            name={'email'}
                            source={'email'}
                            label={'main.navigation.email'}
                            validate={[required(), email()]}
                        />
                    </FormInputsSeparator>

                    <ButtonsBlock>
                        <Button
                            disabled={isLoadingGetAlphaId || isLoadingCreate}
                            label={'ra.action.cancel'}
                            onClick={onClose}
                        />

                        <LoadingButton
                            data-cy={'submit-agency'}
                            loading={
                                isLoadingUser ||
                                isLoadingProfile ||
                                isLoadingCreate ||
                                isLoadingUpdate ||
                                isLoadingGetAlphaId ||
                                isLoadingDefaultClientsGroup
                            }
                            type={'submit'}
                            variant={'contained'}
                        >
                            {translate('ra.action.create')}
                        </LoadingButton>
                    </ButtonsBlock>
                </Box>
            </Form>
        </DialogTemplate>
    );
};

export default CreateAgencyDialog;
